import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DealsQuestionnaireData, Score } from "@/pages/deals/types";
import { Guardrails, GuardrailsPositive } from "@dreamhub/designsystem/icons";
import { ScoreType, ScoreConfig } from "@/pages/deals/scoreConfig";
import GeneralQuestionnaire from "@/components/controls/Questionnaires/GeneralQuestionnaire";
import MappingQuestionnaire from "@/components/controls/Questionnaires/MappingQuestionnaire";
import SimpleQuestionnaire from "@/components/controls/Questionnaires/SimpleQuestionnaire";
import POCQuestionnaire from "@/components/controls/Questionnaires/POCQuestionnaire";
import IconContainer from "@/components/ui/icon-container";
import { isNull } from "lodash";
import { QuestionnaireType } from "@/components/controls/Questionnaires/Questionnaire";
import { showQuestionnaireScoreToast } from "@/pages/deals/components/DealStages/questionnaireHelpers";
import { cn } from "@/utils/utils";
import Banner from "@/components/ui/banner";
import POCSuccessCriteria from "@/components/controls/Questionnaires/POCSuccessCriteria";
import WorkflowDetailPanelFooter from "@/components/shared/WorkflowDetailPanelFooter";
import WorkflowDetailPanelHeader from "@/components/shared/WorkflowDetailPanelHeader";
import WorkflowScore from "@/pages/deals/components/DealStages/components/WorkflowScore";

interface Props {
  dealId: string;
  questionnaire: DealsQuestionnaireData;
  pocQuestionnaire?: DealsQuestionnaireData;
  relatedQuestionnaire?: DealsQuestionnaireData;
  handleRefetch?: () => Promise<void>;
  isLoadingScore?: boolean;
  onOpenRelatedQuestionnaire?: () => void;
  onOpenPocQuestionnaire?: () => void;
}

const QuestionnaireDetailPanel: React.FC<Props> = ({
  dealId,
  questionnaire,
  pocQuestionnaire,
  relatedQuestionnaire,
  handleRefetch,
  isLoadingScore = false,
  onOpenRelatedQuestionnaire,
  onOpenPocQuestionnaire
}) => {
  const { t } = useTranslation();
  const {
    typeValue,
    name,
    score,
    responses,
    id,
    kpiValue,
    isGuardrails,
    description,
    questions
  } = questionnaire;
  const [localScore, setLocalScore] = useState<Score | null>(null);

  useEffect(() => {
    setLocalScore(score || null);
  }, [score]);

  const isNotCompleted =
    localScore?.scoreText?.toLowerCase() === ScoreConfig.PENDING &&
    isNull(localScore.scoreResult);
  const isStakeholdersApproval = kpiValue === ScoreType.STAKEHOLDERS_APPROVAL;
  const isGeneral = kpiValue === ScoreType.GENERAL;
  const isPassed = localScore?.scoreText?.toLowerCase() === ScoreConfig.PASS;

  const isGeneralQuestionnaire = typeValue === QuestionnaireType.GENERAL;
  const hasRequiredFields =
    isGeneralQuestionnaire &&
    (questions?.some((question) => question.isMandatory) ?? false);
  const showGuardrailIcon = isGuardrails || hasRequiredFields;

  const isPocNotEnded =
    isStakeholdersApproval &&
    pocQuestionnaire?.score?.scoreText?.toLowerCase() !== "pass";

  const pocHasNoSuccessCriteria =
    isStakeholdersApproval &&
    pocQuestionnaire?.questions &&
    !pocQuestionnaire.questions.some((q) => q.isSuccessCriteria);

  const handleUpdateQuestionnaireScore = (
    newScore: Score | null,
    hideErrorToast: boolean = false
  ) => {
    showQuestionnaireScoreToast({
      previousScore: localScore,
      newScore,
      questionnaireName: name,
      questionnaireId: id,
      kpiValue: kpiValue || "",
      hideErrorToast,
      t
    });

    setLocalScore(newScore);
  };

  const renderQuestionnaireName = () => {
    if (isStakeholdersApproval && pocQuestionnaire?.name) {
      return t("deals.dealView.stakeholdersApprovalName", {
        name,
        pocName: pocQuestionnaire?.name
      });
    }
    return name;
  };

  const totalCount = questions?.length || 0;
  const requiredCount = questions?.filter((q) => q.isMandatory).length || 0;
  const answeredCount =
    questions?.filter((q) => {
      const response = responses?.[q.id];
      return (
        response?.response?.value !== undefined &&
        response?.response?.value !== null &&
        response?.response?.value !== ""
      );
    }).length || 0;
  const answeredRequiredCount =
    questions?.filter((q) => {
      if (!q.isMandatory) return false;
      const response = responses?.[q.id];
      return (
        response?.response?.value !== undefined &&
        response?.response?.value !== null &&
        response?.response?.value !== ""
      );
    }).length || 0;

  return (
    <div className="w-full h-full bg-surface-widget-card rounded-2xl flex flex-col">
      <WorkflowDetailPanelHeader
        title={renderQuestionnaireName()}
        description={description}
        rightSlot={
          <>
            <WorkflowScore
              isLoadingScore={isLoadingScore}
              isNotCompleted={isNotCompleted}
              isGeneral={isGeneral}
              isStakeholdersApproval={isStakeholdersApproval}
              scoreText={localScore?.scoreText || ""}
              scoreType={kpiValue as ScoreType}
            />
            {showGuardrailIcon && (
              <IconContainer
                data-test={`guardrail-workflow-${typeValue}`}
                size="7"
                color={isPassed ? "positive" : "warning"}
                className={cn(
                  " rounded-md",
                  isPassed ? "bg-badges-positive" : "bg-badges-warning"
                )}
              >
                {isPassed ? <GuardrailsPositive /> : <Guardrails />}
              </IconContainer>
            )}
          </>
        }
      />
      <div
        className={cn(
          "p-8 h-full overflow-y-auto border-b border-border-border1",
          typeValue === QuestionnaireType.POC && "p-0",
          typeValue === QuestionnaireType.APPROVAL && "p-6",
          (isPocNotEnded || pocHasNoSuccessCriteria) && "pt-4"
        )}
      >
        {isStakeholdersApproval && (
          <POCSuccessCriteria
            pocName={pocQuestionnaire?.name}
            hasNoSuccessCriteria={Boolean(pocHasNoSuccessCriteria)}
            onAddSuccessCriteria={onOpenPocQuestionnaire || (() => {})}
            pocQuestions={pocQuestionnaire?.questions}
            pocResponses={pocQuestionnaire?.responses}
          />
        )}
        {(isPocNotEnded || pocHasNoSuccessCriteria) && (
          <Banner
            variant="error"
            title={t(
              `deals.dealView.${pocHasNoSuccessCriteria ? "stakeholdersApprovalNoSuccessCriteria" : "stakeholdersApprovalPocNotEnded"}`,
              {
                pocName: pocQuestionnaire?.name || ""
              }
            )}
            className="p-2 h-fit mb-4"
          />
        )}
        {(() => {
          switch (typeValue) {
            case QuestionnaireType.MAPPING:
              return (
                <MappingQuestionnaire
                  dealId={dealId}
                  questionnaire={questionnaire}
                  responses={responses}
                  handleUpdateQuestionnaireScore={(newScore) =>
                    handleUpdateQuestionnaireScore(newScore, true)
                  }
                />
              );
            case QuestionnaireType.POC:
              return (
                <POCQuestionnaire
                  dealId={dealId}
                  questionnaire={questionnaire}
                  responses={responses}
                  handleUpdateQuestionnaireScore={
                    handleUpdateQuestionnaireScore
                  }
                />
              );
            case QuestionnaireType.GENERAL:
              return (
                <GeneralQuestionnaire
                  dealId={dealId}
                  questionnaire={questionnaire}
                  responses={responses}
                  handleUpdateQuestionnaireScore={(newScore) =>
                    handleUpdateQuestionnaireScore(newScore, true)
                  }
                  handleRefetch={handleRefetch}
                  disabled={!!(isPocNotEnded || pocHasNoSuccessCriteria)}
                />
              );
            case QuestionnaireType.SIMPLE:
            default:
              return (
                <SimpleQuestionnaire
                  dealId={dealId}
                  questionnaire={questionnaire}
                  responses={responses}
                  handleUpdateQuestionnaireScore={
                    handleUpdateQuestionnaireScore
                  }
                  relatedQuestionnaire={relatedQuestionnaire}
                  handleRefetch={handleRefetch}
                  onOpenRelatedQuestionnaire={onOpenRelatedQuestionnaire}
                  disabled={!!(isPocNotEnded || pocHasNoSuccessCriteria)}
                  addApprovalButtonDisabled={isPocNotEnded}
                />
              );
          }
        })()}
      </div>
      <WorkflowDetailPanelFooter
        translationPrefix="deals.dealView.questionnaireFooter"
        answeredCount={answeredCount}
        answeredRequiredCount={answeredRequiredCount}
        requiredCount={requiredCount}
        totalCount={totalCount}
      />
    </div>
  );
};

export default QuestionnaireDetailPanel;
